<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import type { SearchSuggestion } from '@/types';

const { t } = useI18n();

defineProps<{
  suggestions: SearchSuggestion[];
  selectedIndex: number;
  loading?: boolean;
  listboxId: string;
}>();

defineEmits<{
  (e: 'select', suggestion: SearchSuggestion): void;
  (e: 'hover', index: number): void;
}>();
</script>

<template>
  <div class="absolute z-50 mt-2 w-full rounded-lg border border-noir-700 bg-noir-900 shadow-xl">
    <div v-if="loading" class="px-4 py-3 text-sm text-noir-300" role="status" aria-live="polite">
      {{ t('search.loadingSuggestions') }}
    </div>

    <ul v-else :id="listboxId" role="listbox" class="max-h-80 overflow-y-auto py-1">
      <li
        v-for="(suggestion, index) in suggestions"
        :key="suggestion.id"
        role="presentation"
      >
        <button
          :id="`${listboxId}-option-${index}`"
          type="button"
          role="option"
          :aria-selected="selectedIndex === index"
          :class="[
            'min-h-[44px] w-full cursor-pointer px-4 py-2 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-400/60 focus-visible:ring-inset',
            selectedIndex === index ? 'bg-gold-900/20 text-gold-300' : 'text-cream-100 hover:bg-noir-800',
          ]"
          @mouseenter="$emit('hover', index)"
          @focus="$emit('hover', index)"
          @click="$emit('select', suggestion)"
        >
          <p class="font-medium">{{ suggestion.title }}</p>
          <p class="text-xs uppercase tracking-wide text-noir-300">{{ t('search.suggestionType.' + suggestion.type) }}</p>
        </button>
      </li>
    </ul>
  </div>
</template>
